import { useContext, useReducer, useEffect } from 'react';
import { Context } from './Context';
import initialState from './data';
import { 
	getStateFromLocalStore, 
	saveStateToLocalStore,  
	createUUID,
    groupBy
} from '../lib/methods';

export const storeReducer = (state, action) =>  {	
    switch (action.type) {
        case 'init':
        case 'set':
            state = action.value;           
            return state;            
        case 'reset':
            state = initialState;
            return state;
        default: 
            console.log(`Processing unknown reducer type ${action.type}`);
            return state;            
    }    
}

export const useStore = (channelName) => {

    // https://github.com/trevoro/use-broadcast/blob/master/index.js
    // store state
    const [ state, dispatch] = useReducer(storeReducer, initialState);

    // backup state for sharing data between components
    const { setContextState } = useContext(Context);

    // broadcast channel
    const channel = new BroadcastChannel( channelName );   
   
    /***
     * Check if state is cached in browser store
     */ 
    useEffect( () => {

    	// retrieve state from local storage on load
        const cachedState = getStateFromLocalStore();      
        if(cachedState) {           
            dispatch({ type: "init", value: cachedState });
            setContextState(cachedState);

        // add default state to local storage
        } else {        
        	saveStateToLocalStore(initialState);    
       	    setContextState(initialState);   
        }       
    }, [ setContextState ])
    
    /***
     * Losten to Store Updates 
     */
    channel.onmessage = function(event) {        
        let { data } = event;   

        // save to local storage             
        saveStateToLocalStore(data);

        // update reducer state 
        dispatch( {type: 'set', value: data });        

        // update context state 
        setContextState(data);
       
    };   

    const addOrder = (product) => {    

        let {products,  totalAmount:a, totalItems: t, orders: currentOrders } = state;

        // clone product(s)
        const _product = {...product};
        const _products = [...products];
        
        // create order object
        let o = {
            ...product,
            orderID: createUUID(),
            createdAt: new Date(),
            lastModified: null
        };

        // determine processID          
        o.pID = (currentOrders.length + 1);         

        // merge new order
        currentOrders = [...currentOrders, o];  

        // sort orders
        currentOrders.sort((a,b) => (a.name < b.name) ? -1 : ((b.name > a.name) ? 1 : 0))

        // count orders of this item
        let sorted = groupBy([...currentOrders],'id');
        _product.count = sorted[o.id].length;

        // signal that this product has been ordered
        _product.inBasket = true;       

        // update products                  
        const index = _products.findIndex( item => _product.id === item.id);        
        _products.splice(index,1, _product);         

        // store update 
        const newState = { 
            ...state,               
            products: _products,
            orders: currentOrders, 
            totalAmount: (parseFloat(a) + parseFloat(product.price)).toFixed(2),
            totalItems: (t + 1)
        };
       
        channel.postMessage( newState );        
    }

    const removeOrder = (orderID) => {
       
        let {totalAmount:a, totalItems: t, orders, products } = state;

        // clone state && products && products          
        const _products = [...products];            
        const _orders = [...orders];
        
        // get cloned order
        const _order = {..._orders.find( o => o.orderID === orderID)};

        // define productID 
        const { id: productID} = _order;

        // get product
        const _product = {..._products.find( p => p.id === productID) };                    

        // remove order from orders array
        const newOrders = _orders.filter( order => order.orderID !== orderID);

        // reset product-in-basket count
        _product.count = newOrders.filter( o => o.id === productID).length;

        // update of this product is in basket
        _product.inBasket = _products.some( product => product.id === productID )       

        // update products                  
        const index = _products.findIndex( item => _product.id === item.id);        
        _products.splice(index, 1, _product);                           
    
        // reduce total items and calc new totaal amount
        t = newOrders.length;                   
        a = (a - _product.price);           
    
        // update store through channel
        const newState = { 
            ...state, 
            orders: [ ...newOrders ], 
            products: [..._products],
            totalAmount: a, 
            totalItems: t 
        };          

        channel.postMessage( newState );           
    }

    const removeCategoryOfItems = (productID) => {

        let {totalAmount:a, totalItems: t, orders, products } = state;

            // clone state && products && products          
        const _products = [...products];            
        const _orders = [...orders];
        
        // find product
        const _product = {...products.find( product => product.id === productID)};

        // signal that this product has been removed from basket
        _product.inBasket = false;

        // reset product-in-basket count
        _product.count = 0;

        // update products                  
        const index = _products.findIndex( item => _product.id === item.id);        
        _products.splice(index,1, _product);            

        // remove all related products from orders array
        const newOrders = _orders.filter( order => order.id !== productID);

        // sort orders
        newOrders.sort((a,b) => (a.name < b.name) ? -1 : ((b.name > a.name) ? 1 : 0));
    
        // reduce total items and calc new totaal amount
        t = newOrders.length;                   
        a = (a - _product.price);   

        // update store through channel
        const newState = { 
            ...state, 
            orders: [ ...newOrders ], 
            products: [..._products],
            totalAmount: a, 
            totalItems: t 
        };          

        channel.postMessage( newState );                
    }

    const resetStore = () => channel.postMessage( initialState );

    return [ 
        state,        
        addOrder,
        removeOrder,
        removeCategoryOfItems,
        resetStore
    ];
}